import {Platform} from "@variocube/app-api";
import {
	generate,
	generateCode,
	getRandomCompany,
	getRandomElement,
	getRandomFirstName,
	getRandomLastName,
	getRandomTitle,
} from "@variocube/app-common";
import {
	Account,
	AccountSettings,
	AssignedLocks,
	Capability,
	Case,
	Cases,
	ConfigKey,
	Plan,
	PlanCapability,
	PostBoxIdDigits,
	PostBoxIdLength,
	RecipientSelection,
} from "./model";

function getRandomPostBoxId() {
	return generate(PostBoxIdLength, () => getRandomElement(PostBoxIdDigits)).join("");
}

// Type that implements both types that are provided from the backend
type PostBox = Account;

function generatePostBox(ordinal: number) {
	const firstName = getRandomFirstName();
	const lastName = getRandomLastName();
	return {
		id: ordinal,
		postBoxId: getRandomPostBoxId(),
		firstName: firstName,
		lastName: lastName,
		companyName: getRandomCompany(),
		title: getRandomTitle(),
		displayAbbr: `${ordinal}`,
		displayName: lastName,
		displayDescription: firstName,
		capabilities: [Capability.InboundMail, Capability.OutboundMail, Capability.Transfer],
		specialNeeds: ordinal == 1,
	};
}

export async function loadMockData(platform: Platform) {
	const PostBoxes: PostBox[] = generate(100, generatePostBox);

	// args for testing custom branding
	const colorPrimary = platform.getArg("colorPrimary");
	const theme = colorPrimary ? {colorPrimary} : undefined;
	const background = platform.getArg("background");

	let backgroundImage = undefined;
	if (background) {
		const backgroundResponse = await window.fetch(
			"data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mPccePjfwAIUwOCOf5cIwAAAABJRU5ErkJggg==",
		);
		backgroundImage = await backgroundResponse.blob();
	}

	const cases: Cases = {};

	for (let i = 0; i < 3; i++) {
		cases[generateCode(5)] = {
			dispatch: {
				postBoxId: getRandomElement(PostBoxes).postBoxId,
				dispatchId: 1234,
			},
		} as Case;
	}
	for (let i = 0; i < 3; i++) {
		cases[generateCode(5)] = {
			dispatch: {
				postBoxId: getRandomElement(PostBoxes).postBoxId,
				planId: 1,
			},
		} as Case;
	}
	for (let i = 0; i < 3; i++) {
		cases[generateCode(5)] = {
			transfer: {
				postBoxId: getRandomElement(PostBoxes).postBoxId,
				transferId: 1234,
			},
		} as Case;
	}

	const mockConfig = {
		[ConfigKey.inputTrackingNumber]: "disabled", // "required", "optional", "disabled"

		[ConfigKey.Carriers]: [
			{abbreviation: "UPS", name: "United Parcel System"},
			{abbreviation: "Post", name: "Österreichische Post AG"},
			{"name": "GLS"},
			{"name": "DHL"},
			{"name": "DPD"},
			{"name": "Sonstige"},
		],

		[ConfigKey.Sizes]: [
			"M",
			"S",
			"L",
			// 'Unavailable', uncomment to test unavailable box type
		],

		[ConfigKey.LockMapping]: {
			lock1: "lock12",
			lock2: "lock12",
			lock3: "lock12",
			lock4: "lock12",
			lock5: "lock12",
			lock6: "lock12",
			lock7: "lock12",
			lock8: "lock12",
			lock9: "lock12",
			lock10: "lock12",
			lock11: "lock12",
		},

		// Use this setting to switch between recipient selection modes
		// Please do not change this setting in production, sellers should be able
		// to use our mock setup during demos.
		[ConfigKey.RecipientSelection]: RecipientSelection.List,

		[ConfigKey.Accounts]: PostBoxes,

		[ConfigKey.Theme]: theme,

		[ConfigKey.PoBoxUrl]: "https://pobox.variocube.com/api/post-box/query",

		backgroundImage,

		[ConfigKey.Cases]: cases,

		plans: {
			"1": {
				dispatchPickupCode: "54321",
				capabilities: [PlanCapability.InboundMail, PlanCapability.OutboundMail],
			} as Plan,
		},

		userAccessTokens: {
			"42069": {
				postBoxId: getRandomElement(PostBoxes).postBoxId,
				code: "42069",
				userId: "badge-user-1",
				cubeId: "localhost",
			},
			"badge1": {
				postBoxId: getRandomElement(PostBoxes).postBoxId,
				token: "badge1",
				userId: "badge-user-1",
				cubeId: "localhost",
			},
		},
		speaker: true,
		directBoxSelection: false,
	};

	//
	// Add assigned locks and fixed pickup codes
	//
	const assignedLocks: AssignedLocks = {};
	const accountSettings: AccountSettings = {};

	const boxes = platform.boxes;

	for (let i = 0; i < 3; i++) {
		const postBox = getRandomElement(PostBoxes);
		const pickupCode = generateCode(5);
		accountSettings[postBox.postBoxId] = {
			fixedPickupCode: pickupCode,
			doorBellSerialNr: String(i + 1),
		};
		assignedLocks[pickupCode] = [getRandomElement(boxes).lock];
	}

	// Write relevant config to console
	console.info(ConfigKey.Accounts, mockConfig[ConfigKey.Accounts]);
	console.info(ConfigKey.AssignedLocks, assignedLocks);
	console.info(ConfigKey.AccountSettings, accountSettings);

	// test removal and addition to accounts key
	if (platform.getArg("emulateAccountChange")) {
		window.setTimeout(() => {
			console.log("Emulating account change");
			// remove one
			PostBoxes.shift();
			// add 1000
			PostBoxes.push(...generate(1000, ordinal => generatePostBox(10000 + ordinal)));
		}, 10000);
	}

	return {
		...mockConfig,
		assignedLocks,
		accountSettings,
	};
}
